import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Users, FileText, ToggleLeft, ToggleRight, Save, DollarSign, CreditCard, ExternalLink, Package, X, Plus, Check, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import api from '../api/client';
import toast from 'react-hot-toast';

const unitLabel = (u) => ({
  per_query: '/query', per_message: '/msg', per_minute: '/min', per_page: '/page', per_gb_month: '/GB-mo',
}[u] || u);

function ServiceRow({ svc, tenantId, onUpdate, addon }) {
  const [enabled, setEnabled] = useState(svc.is_enabled || false);
  const [markup, setMarkup] = useState(svc.custom_markup_pct ?? '');
  const [limit, setLimit] = useState(svc.monthly_limit ?? '');
  const [overagePolicy, setOveragePolicy] = useState(svc.overage_policy || 'hard_block');
  const [overageMarkup, setOverageMarkup] = useState(svc.overage_markup_pct ?? '200');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    const newVal = !enabled;
    setEnabled(newVal);
    try {
      await api.put(`/platform/tenants/${tenantId}/services/${svc.id}`, { is_enabled: newVal });
      toast.success(`${svc.name} ${newVal ? 'enabled' : 'disabled'}`);
      onUpdate();
    } catch (err) {
      setEnabled(!newVal);
      toast.error('Failed to update');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/platform/tenants/${tenantId}/services/${svc.id}`, {
        custom_markup_pct: markup === '' ? null : parseFloat(markup),
        monthly_limit: limit === '' ? null : parseInt(limit),
        overage_policy: overagePolicy,
        overage_markup_pct: parseFloat(overageMarkup) || 200,
      });
      setDirty(false);
      toast.success(`${svc.name} settings saved`);
      onUpdate();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const usage = parseFloat(svc.current_month_usage) || 0;
  const limitVal = limit === '' ? null : parseInt(limit);
  const addonQty = addon ? parseInt(addon.included_quantity) || 0 : 0;
  const totalLimit = limitVal !== null ? limitVal + addonQty : (addonQty > 0 ? addonQty : null);
  const usagePct = totalLimit ? Math.min((usage / totalLimit) * 100, 100) : 0;
  const effectiveMarkup = markup !== '' ? parseFloat(markup) : parseFloat(svc.default_markup_pct);
  const isOverridden = markup !== '' && markup !== null;

  return (
    <tr className="border-b border-gray-100">
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900">{svc.name}</p>
        <p className="text-xs text-gray-500">{svc.current_provider || 'No provider'}</p>
      </td>
      <td className="px-4 py-3 text-center">
        <button onClick={toggle} className="p-1">
          {enabled ? <ToggleRight className="w-6 h-6 text-green-600" /> : <ToggleLeft className="w-6 h-6 text-gray-400" />}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <input type="number" step="0.01" value={markup} placeholder={String(parseFloat(svc.default_markup_pct))}
            onChange={(e) => { setMarkup(e.target.value); setDirty(true); }}
            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <span className="text-sm text-gray-500">%</span>
          {isOverridden && <span className="ml-1 w-2 h-2 bg-orange-400 rounded-full" title="Custom override" />}
        </div>
        <p className="text-xs text-gray-400 mt-0.5">Effective: {effectiveMarkup.toFixed(0)}%</p>
      </td>
      <td className="px-4 py-3">
        <input type="number" value={limit} placeholder="Unlimited"
          onChange={(e) => { setLimit(e.target.value); setDirty(true); }}
          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
        <p className="text-xs text-gray-400 mt-0.5">{unitLabel(svc.base_cost_unit)}</p>
      </td>
      <td className="px-4 py-3">
        {addon ? (
          <div>
            <span className="text-sm font-medium text-green-700">+{addonQty.toLocaleString()}</span>
            <p className="text-xs text-gray-400">{addon.addon_name}</p>
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <select value={overagePolicy} onChange={(e) => { setOveragePolicy(e.target.value); setDirty(true); }}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
          <option value="hard_block">Hard Block</option>
          <option value="allow_overage">Allow Overage</option>
        </select>
        {overagePolicy === 'allow_overage' && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs text-gray-500">@</span>
            <input type="number" step="1" value={overageMarkup} onChange={(e) => { setOverageMarkup(e.target.value); setDirty(true); }}
              className="w-16 px-2 py-0.5 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
            <span className="text-xs text-gray-500">%</span>
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm font-medium text-gray-700">
          {usage.toLocaleString()}{totalLimit ? ` / ${totalLimit.toLocaleString()}` : ''}
        </div>
        {totalLimit && (
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div className={`h-1.5 rounded-full ${usagePct >= 90 ? 'bg-red-500' : usagePct >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${usagePct}%` }} />
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {dirty && (
          <button onClick={save} disabled={saving} className="flex items-center gap-1 px-3 py-1 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
            <Save className="w-3 h-3" /> {saving ? '...' : 'Save'}
          </button>
        )}
      </td>
    </tr>
  );
}

function ManageAddonsModal({ tenantId, tenantAddons, availableAddons, onClose, onRefresh }) {
  const [activating, setActivating] = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  const activeMap = {};
  for (const a of tenantAddons) activeMap[a.addon_service_slug] = a;

  const activateAddon = async (addonDef) => {
    setActivating(addonDef.service);
    try {
      await api.post(`/platform/tenants/${tenantId}/addons`, {
        addon_service_slug: addonDef.service,
        addon_name: addonDef.display_name,
        monthly_price: addonDef.price,
        included_quantity: addonDef.included_quantity,
        unit: addonDef.unit,
      });
      toast.success(`${addonDef.display_name} activated`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to activate');
    } finally {
      setActivating(null);
    }
  };

  const deactivateAddon = async (addonId, name) => {
    setDeactivating(addonId);
    try {
      await api.delete(`/platform/tenants/${tenantId}/addons/${addonId}`);
      toast.success(`${name} deactivated`);
      onRefresh();
    } catch (err) {
      toast.error('Failed to deactivate');
    } finally {
      setDeactivating(null);
    }
  };

  const totalAddonCost = tenantAddons.reduce((s, a) => s + (parseFloat(a.monthly_price) || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[650px] max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage Add-ons</h2>
            <p className="text-sm text-gray-500">Toggle service add-ons for this tenant</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-4">
          {availableAddons.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No add-ons configured. Add them in Settings.</p>
          ) : (
            <div className="space-y-3">
              {availableAddons.map(addonDef => {
                const active = activeMap[addonDef.service];
                return (
                  <div key={addonDef.service}
                    className={`flex items-center justify-between p-4 rounded-xl border ${active ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{addonDef.display_name}</span>
                        {active && <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>}
                      </div>
                      <p className="text-sm text-gray-500">
                        +{addonDef.included_quantity.toLocaleString()} {addonDef.unit} — ${addonDef.price}/mo
                      </p>
                    </div>
                    <div>
                      {active ? (
                        <button onClick={() => deactivateAddon(active.id, active.addon_name)}
                          disabled={deactivating === active.id}
                          className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50">
                          {deactivating === active.id ? '...' : 'Remove'}
                        </button>
                      ) : (
                        <button onClick={() => activateAddon(addonDef)}
                          disabled={activating === addonDef.service}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 disabled:opacity-50">
                          <Plus className="w-3 h-3" /> {activating === addonDef.service ? '...' : 'Add'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            <span className="font-medium">Active add-ons:</span> {tenantAddons.length} — <span className="font-semibold">${totalAddonCost.toFixed(0)}/mo</span>
          </div>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Done</button>
        </div>
      </div>
    </div>
  );
}

function StripeSection({ tenantId, subscription, stripeCustomerId, stripeConfigured, onRefresh }) {
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [payments, setPayments] = useState(null);
  const [showPayments, setShowPayments] = useState(false);

  const createCustomer = async () => {
    setCreatingCustomer(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenantId}/stripe-customer`);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Stripe customer created: ${data.customer_id}`);
        onRefresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const loadPayments = async () => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}/payments`);
      setPayments(data);
      setShowPayments(true);
    } catch {
      toast.error('Failed to load payments');
    }
  };

  const subStatusColors = {
    active: 'bg-green-100 text-green-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-red-100 text-red-700',
    canceled: 'bg-gray-100 text-gray-600',
    incomplete: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Payment & Subscription</h2>
        </div>
      </div>
      <div className="p-6">
        {!stripeConfigured ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-1">Stripe is not configured</p>
            <p className="text-xs text-gray-400">Set STRIPE_SECRET_KEY in environment to enable payment processing</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stripe Customer */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stripe Customer</p>
                {stripeCustomerId ? (
                  <p className="text-sm font-mono text-gray-900">{stripeCustomerId}</p>
                ) : (
                  <p className="text-sm text-gray-400">No Stripe customer linked</p>
                )}
              </div>
              {!stripeCustomerId && (
                <button onClick={createCustomer} disabled={creatingCustomer}
                  className="px-3 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
                  {creatingCustomer ? 'Creating...' : 'Create Customer'}
                </button>
              )}
            </div>

            {/* Subscription */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500 mb-2">Subscription</p>
              {subscription ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{subscription.plan_name}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${subStatusColors[subscription.status] || 'bg-gray-100'}`}>
                        {subscription.status}
                      </span>
                      {subscription.cancel_at_period_end && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-600">Canceling</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      ${parseFloat(subscription.monthly_base_price || 0).toFixed(2)}/mo
                      {subscription.current_period_end && ` — renews ${new Date(subscription.current_period_end).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No active subscription</p>
              )}
            </div>

            {/* Payment History */}
            <div className="border-t border-gray-100 pt-4">
              {!showPayments ? (
                <button onClick={loadPayments} className="text-sm text-brand-600 hover:text-brand-700 font-medium">
                  View Payment History
                </button>
              ) : payments && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Payment History</h4>
                  {payments.payments?.length === 0 ? (
                    <p className="text-sm text-gray-400">No payments found</p>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-auto">
                      {(payments.payments || []).slice(0, 10).map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 text-sm border-b border-gray-50">
                          <div>
                            <span className="text-gray-700">{p.number || new Date(p.created || p.cycle_start).toLocaleDateString()}</span>
                            {p.hosted_invoice_url && (
                              <a href={p.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-brand-600 hover:text-brand-700">
                                <ExternalLink className="w-3 h-3 inline" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                              p.status === 'paid' ? 'bg-green-100 text-green-700' :
                              p.status === 'open' || p.status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{p.status}</span>
                            <span className="font-medium text-gray-900">${(p.amount || parseFloat(p.total_billed) || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [billing, setBilling] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [tenantAddons, setTenantAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchTenant = useCallback(async () => {
    try {
      const [tenantRes, servicesRes, billingRes, subRes, addonsRes, availRes, plansRes] = await Promise.all([
        api.get(`/platform/tenants/${id}`),
        api.get(`/platform/tenants/${id}/services`),
        api.get(`/platform/tenants/${id}/billing`).catch(() => ({ data: null })),
        api.get(`/platform/tenants/${id}/subscription`).catch(() => ({ data: {} })),
        api.get(`/platform/tenants/${id}/addons`).catch(() => ({ data: { addons: [] } })),
        api.get('/platform/addons').catch(() => ({ data: { addons: [] } })),
        api.get('/platform/stripe/config').catch(() => ({ data: { plans: [] } })),
      ]);
      setTenant(tenantRes.data.tenant);
      setUsers(tenantRes.data.users);
      setServices(servicesRes.data.services);
      setBilling(billingRes.data);
      setSubscription(subRes.data?.subscription || null);
      setStripeConfigured(subRes.data?.stripe_configured || false);
      setTenantAddons((addonsRes.data?.addons || []).filter(a => a.is_active));
      setAvailableAddons(availRes.data?.addons || []);
      setAvailablePlans(plansRes.data?.plans || []);
    } catch (err) {
      toast.error('Failed to load tenant');
      navigate('/tenants');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchTenant(); }, [fetchTenant]);

  const refreshServices = async () => {
    try {
      const { data } = await api.get(`/platform/tenants/${id}/services`);
      setServices(data.services);
    } catch { /* ignore */ }
  };

  const toggleActive = async () => {
    try {
      const { data } = await api.put(`/platform/tenants/${id}`, { is_active: !tenant.is_active });
      setTenant(prev => ({ ...prev, is_active: data.tenant.is_active }));
      toast.success(`Tenant ${data.tenant.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const changePlan = async (planName) => {
    setChangingPlan(true);
    try {
      await api.put(`/platform/tenants/${id}/plan`, { plan_name: planName });
      toast.success('Plan updated');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="h-32 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tenants" className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-sm text-gray-500">{tenant.slug}</p>
        </div>
        <button onClick={toggleActive}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tenant.is_active ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
          {tenant.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>

      {/* Section 1: Tenant Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${tenant.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {tenant.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div>
            <p className="text-sm text-gray-500">Users</p>
            <p className="flex items-center gap-1 mt-1 font-semibold text-gray-900"><Users className="w-4 h-4" /> {tenant.user_count}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Records</p>
            <p className="flex items-center gap-1 mt-1 font-semibold text-gray-900"><FileText className="w-4 h-4" /> {tenant.record_count}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="mt-1 font-semibold text-gray-900">{format(new Date(tenant.created_at), 'MMM d, yyyy')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Services Enabled</p>
            <p className="mt-1 font-semibold text-gray-900">{services.filter(s => s.is_enabled).length} / {services.length}</p>
          </div>
        </div>
      </div>

      {/* Section 2: Plan + Add-ons Summary + Service Switchboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        {/* Plan + Add-ons Summary Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Service Switchboard</h2>
              <p className="text-sm text-gray-500">Configure which platform services are enabled for this tenant</p>
            </div>
            <button onClick={() => setShowAddonModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100">
              <Package className="w-4 h-4" /> Manage Add-ons
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {/* Change Plan dropdown */}
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Plan:</span>
              <select
                value={subscription?.plan_id || ''}
                onChange={e => e.target.value && changePlan(e.target.value)}
                disabled={changingPlan}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
              >
                {!subscription && <option value="">No plan assigned</option>}
                {availablePlans.map(p => (
                  <option key={p.name} value={p.name}>
                    {p.display_name || p.name} — {p.monthly_price === 0 ? 'Free' : `$${p.monthly_price}/mo`}
                  </option>
                ))}
              </select>
              {changingPlan && <span className="text-xs text-gray-400">Updating...</span>}
            </div>
            {tenantAddons.length > 0 && (
              <span className="text-gray-700">
                <span className="font-medium">Add-ons:</span>{' '}
                {tenantAddons.map(a => `${a.addon_name} ($${parseFloat(a.monthly_price).toFixed(0)})`).join(', ')}
              </span>
            )}
            <span className="text-gray-900 font-semibold">
              Monthly Total: ${(
                (parseFloat(subscription?.monthly_base_price) || 0) +
                tenantAddons.reduce((s, a) => s + (parseFloat(a.monthly_price) || 0), 0)
              ).toFixed(0)}/mo
            </span>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Service</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Enabled</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Markup</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Plan Limit</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Add-on</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Over Limit</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Usage (Month)</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600"></th>
            </tr>
          </thead>
          <tbody>
            {services.map(svc => {
              const addon = tenantAddons.find(a => a.addon_service_slug === svc.slug);
              return <ServiceRow key={svc.id} svc={svc} tenantId={id} onUpdate={refreshServices} addon={addon} />;
            })}
          </tbody>
        </table>
      </div>

      {/* Manage Add-ons Modal */}
      {showAddonModal && (
        <ManageAddonsModal
          tenantId={id}
          tenantAddons={tenantAddons}
          availableAddons={availableAddons}
          onClose={() => setShowAddonModal(false)}
          onRefresh={fetchTenant}
        />
      )}

      {/* Section 3: Billing */}
      {billing && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
          </div>
          <div className="p-6">
            {/* Current Cycle */}
            {billing.current_cycle && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-700">Current Cycle</h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                    {billing.current_cycle.status}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {new Date(billing.current_cycle.cycle_start).toLocaleDateString()} — {new Date(billing.current_cycle.cycle_end).toLocaleDateString()}
                </p>
                {billing.current_usage.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Service</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Usage</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cost</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Billed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.current_usage.map((u, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-900">{u.service_name}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{u.usage.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right text-gray-700">${(parseFloat(u.provider_cost) || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium text-gray-900">${(parseFloat(u.billed) || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-right">${billing.current_usage.reduce((s, u) => s + (parseFloat(u.provider_cost) || 0), 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">${billing.current_usage.reduce((s, u) => s + (parseFloat(u.billed) || 0), 0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-400">No usage this cycle</p>
                )}
              </div>
            )}

            {/* Monthly Trend */}
            {billing.monthly_trend && billing.monthly_trend.length > 0 && (
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Monthly Cost Trend</h3>
                <div className="flex items-end gap-1 h-16">
                  {billing.monthly_trend.map((m, i) => {
                    const max = Math.max(...billing.monthly_trend.map(t => t.billed), 1);
                    const h = (m.billed / max) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-green-500 rounded-t opacity-80" style={{ height: `${h}%`, minHeight: m.billed > 0 ? '4px' : '0' }}
                          title={`${m.month}: $${m.billed.toFixed(2)}`} />
                        <p className="text-[10px] text-gray-400 mt-1">{m.month.slice(5)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past Cycles */}
            {billing.past_cycles && billing.past_cycles.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Past Cycles</h3>
                <div className="space-y-1">
                  {billing.past_cycles.filter(c => c.status !== 'open').map(c => (
                    <div key={c.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 text-sm">
                      <span className="text-gray-700">
                        {new Date(c.cycle_start).toLocaleDateString()} — {new Date(c.cycle_end).toLocaleDateString()}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                          c.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                          c.status === 'paid' ? 'bg-green-100 text-green-700' :
                          c.status === 'invoiced' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                        }`}>{c.status}</span>
                        <span className="font-medium text-gray-900">${(parseFloat(c.total_billed) || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 4: Payment & Subscription */}
      <StripeSection
        tenantId={id}
        subscription={subscription}
        stripeCustomerId={tenant.stripe_customer_id}
        stripeConfigured={stripeConfigured}
        onRefresh={fetchTenant}
      />

      {/* Section 5: Users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Users ({users.length})</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-gray-100">
                <td className="px-4 py-3 font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                <td className="px-4 py-3 text-gray-700">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_admin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                    {u.is_admin ? 'Admin' : u.role_name || 'User'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{u.last_login_at ? format(new Date(u.last_login_at), 'MMM d, yyyy h:mm a') : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
